import * as THREE from 'three';
import { settings } from '@/config';
import { TOTAL_FRAMES } from '@/animation/SceneSections';

/**
 * CAMERA SMOKE TRANSITION (2026-07-23 v2, user: "it doesn't feel like clouds really cover
 * the camera — I want smoke coming from ALL sides, rotating randomly, with noise, like
 * spiral smoke covering the camera and leaving it — a ~2s custom transition on plane
 * geometry with a shader").
 *
 * ONE fullscreen clip-space quad wearing a custom shader — no sprites, no textures:
 *  - DOMAIN-WARPED FBM (noise displaced by noise) gives real curling, folding smoke;
 *  - a SPIRAL term rotates the noise field ever harder toward the screen centre and drifts
 *    it over time, so the smoke visibly swirls as it closes in;
 *  - coverage is a radial FRONT that advances from every screen edge toward (and past) the
 *    centre as progress rises, with the noise carving wispy fingers into its leading edge —
 *    "smoke coming from all sides", never a flat fade;
 *  - near full cover the alpha is clamped solid (the world swap hides under it), but the
 *    colour keeps churning with the noise, so being inside the smoke still feels alive.
 *
 * The DRIVER is unchanged from the envelope it replaces — progress comes from the current
 * clip frame (covered at frame 0, clear by ~30, re-enveloped 338→352 for the loop), plus a
 * forced mode for checkpoint jumps ({@link envelop}/{@link release}). One system, three
 * uses: the preloader/intro emergence, checkpoint travel, and the end-of-site restart.
 * (A two-style "layered mist bookend" variant was tried and REVERTED same day — the user
 * wants THIS spiral smoke everywhere.)
 */
export class CameraFog {
  readonly group = new THREE.Group();
  private readonly material: THREE.ShaderMaterial;
  /** Start covered only when the FRAME-DRIVEN bookends are on (with the video transition
   *  handling the bookends instead, boot starts clear and only checkpoints envelop). */
  private master = settings.cameraFog.frameDriven ? 1 : 0;
  private forced = false;

  constructor() {
    const cfg = settings.cameraFog;
    this.group.name = 'camera-fog';
    this.group.frustumCulled = false;

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uProgress: { value: 1 },
        uAspect: { value: 1 },
        uColor: { value: new THREE.Color(cfg.color) },
        uScale: { value: cfg.scale },
        uSwirl: { value: cfg.swirl },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          // Clip-space fullscreen quad — ignores every camera matrix, always mats the frame.
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        varying vec2 vUv;
        uniform float uTime;
        uniform float uProgress;
        uniform float uAspect;
        uniform vec3 uColor;
        uniform float uScale;
        uniform float uSwirl;

        float hash(vec2 p) {
          p = fract(p * vec2(234.34, 435.345));
          p += dot(p, p + 34.23);
          return fract(p.x * p.y);
        }
        float vnoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          float a = hash(i);
          float b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0));
          float d = hash(i + vec2(1.0, 1.0));
          return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
        }
        // 5-octave fbm with a rotation each octave so axis alignment never shows.
        float fbm(vec2 p) {
          float v = 0.0;
          float amp = 0.5;
          mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
          for (int i = 0; i < 5; i++) {
            v += amp * vnoise(p);
            p = rot * p * 2.03;
            amp *= 0.5;
          }
          return v;
        }

        void main() {
          vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
          float r = length(p);
          float ang = atan(p.y, p.x);
          // SPIRAL: rotation strengthens toward the centre and drifts with time — the
          // whole smoke field visibly turns as it closes in.
          ang += uSwirl * (1.0 - smoothstep(0.0, 1.0, r)) + uTime * 0.14;
          vec2 sp = vec2(cos(ang), sin(ang)) * r;
          // DOMAIN WARP: fbm displaced by two more fbm fields = curling, folding smoke.
          vec2 q = vec2(
            fbm(sp * uScale + vec2(0.0, uTime * 0.11)),
            fbm(sp * uScale + vec2(5.2, -uTime * 0.13))
          );
          float n = fbm(sp * uScale * 1.15 + q * 1.6 + vec2(uTime * 0.05, 0.0));

          // The FRONT advances from every edge toward (then past) the centre with progress;
          // noise carves wispy fingers into its leading edge.
          float front = mix(1.45, -0.6, uProgress);
          float alpha = smoothstep(front, front + 0.6, r + (n - 0.5) * 1.1);
          // Solid mat near full cover — the world swap is invisible underneath.
          alpha = clamp(alpha + smoothstep(0.78, 0.97, uProgress), 0.0, 1.0);

          // Interior shading: crests catch light, folds sink — alive even at full cover.
          vec3 col = uColor * (0.88 + 0.20 * n + 0.06 * q.x);
          gl_FragColor = vec4(col, alpha);
          #include <colorspace_fragment>
        }
      `,
    });

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    quad.frustumCulled = false;
    quad.renderOrder = 10_000; // after every transparent thing, clouds included
    this.group.add(quad);
    this.group.visible = false;
  }

  /** Target coverage (0-1) for a clip frame — intro emergence 6→30, outro envelope 338→352. */
  private targetFor(frame: number): number {
    const cfg = settings.cameraFog;
    if (frame <= cfg.clearStartFrame) return 1;
    if (frame < cfg.clearEndFrame) {
      return 1 - THREE.MathUtils.smoothstep(frame, cfg.clearStartFrame, cfg.clearEndFrame);
    }
    if (frame <= cfg.coverStartFrame) return 0;
    if (frame < cfg.coverEndFrame) {
      return THREE.MathUtils.smoothstep(frame, cfg.coverStartFrame, cfg.coverEndFrame);
    }
    return 1;
  }

  /** Call once per tick with the LAST SAMPLED clip fraction (every camera path funnels
   *  through ScrollCameraTest.sampleAt, so this is authoritative in intro, scroll,
   *  magnet-glide and teleport alike). */
  update(camera: THREE.Camera, clipFraction: number, delta: number): void {
    const cfg = settings.cameraFog;
    if (!cfg.enabled) {
      this.group.visible = false;
      return;
    }
    // Frame-driven bookends can be disabled (the video transition owns them then) — the
    // shader smoke still answers checkpoint jumps through the forced envelop/release mode.
    const target = this.forced
      ? 1
      : cfg.frameDriven
        ? this.targetFor(clipFraction * TOTAL_FRAMES)
        : 0;
    // Jumps close in a touch quicker (the user is waiting on a click they just made); the
    // part-out and the frame-driven fades run at the normal cinematic rate. Both land the
    // full transition in roughly the user's 2-second budget.
    const rate = this.forced ? cfg.jumpEaseRate : cfg.easeRate;
    this.master += (target - this.master) * (1 - Math.exp(-delta * rate));

    if (target === 0 && this.master < 0.004) {
      this.master = 0;
      this.group.visible = false; // free raster during normal play
      return;
    }
    this.group.visible = true;
    this.material.uniforms.uTime!.value += delta * cfg.speed;
    this.material.uniforms.uProgress!.value = this.master;
    const persp = camera as THREE.PerspectiveCamera;
    this.material.uniforms.uAspect!.value = persp.isPerspectiveCamera ? persp.aspect : 1;
  }

  /** Close the smoke NOW, whatever the frame (checkpoint jump). Pair with {@link release}. */
  envelop(): void {
    this.forced = true;
  }

  /** Hand control back to the frame-driven map — at a clear mid-clip frame the smoke parts. */
  release(): void {
    this.forced = false;
  }

  /** True once the smoke fully mats the view — safe to teleport underneath it. */
  get covered(): boolean {
    return this.master > 0.985;
  }

  dispose(): void {
    this.material.dispose();
    for (const child of this.group.children) {
      const mesh = child as THREE.Mesh;
      mesh.geometry?.dispose();
    }
  }
}
